import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as React from 'react';

import { buttonVariants, ButtonProps } from '../button/button';
import { ArrowDown, ArrowUp, Minus, Plus } from '../icons';
import { cn } from '../../utils';

export interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  indicator?: 'plus-minus' | 'chevron';
}

const AccordionContext = React.createContext<'plus-minus' | 'chevron'>('chevron');

const AccordionButton = ({
  className,
  disabled,
  variant,
  wide,
  size,
  icon,
  children,
}: ButtonProps) => {
  return (
    <div className={cn(buttonVariants({ variant, size, disabled, icon, wide, className }))}>
      {children}
    </div>
  );
};

const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root> & {
    indicator?: 'plus-minus' | 'chevron';
  }
>(({ className, indicator = 'chevron', ...props }, ref) => {
  return (
    <AccordionContext.Provider value={indicator}>
      <AccordionPrimitive.Root
        className={cn('divide-y divide-border-default flex flex-col gap-3', className)}
        ref={ref}
        {...props}
      />
    </AccordionContext.Provider>
  );
});
Accordion.displayName = 'Accordion';

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item className={cn('pb-3', className)} ref={ref} {...props} />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const context = React.useContext(AccordionContext);
  const indicatorChevronIcons = [ArrowDown, ArrowUp];
  const indicatorPlusMinusIcons = [Plus, Minus];

  const indicatorIcons =
    (context === 'plus-minus' && indicatorPlusMinusIcons) || indicatorChevronIcons;

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        className={cn(
          'flex flex-1 items-center justify-between text-sm font-medium transition-all hover:underline text-left [&[data-state=open]>svg]:rotate-180 [&[data-state=open]>.open-icon]:hidden [&[data-state=closed]>.closed-icon]:hidden',
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
        <>
          <AccordionButton className="open-icon" variant="ghost" size="small">
            {indicatorIcons?.[0] &&
              React.createElement(indicatorIcons[0], { className: 'size-4 shrink-0 text-weak' })}
          </AccordionButton>
          <AccordionButton className="closed-icon" variant="ghost" size="small">
            {indicatorIcons?.[1] &&
              React.createElement(indicatorIcons[1], { className: 'size-4 shrink-0 text-weak' })}
          </AccordionButton>
        </>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    className="overflow-hidden text-sm text-weak data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    ref={ref}
    {...props}
  >
    <div className={cn('pt-2', className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { AccordionTrigger, AccordionContent, AccordionItem, Accordion };
